import React, { useRef, useMemo } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { X, Download, Copy } from "lucide-react";
import { toast } from "sonner";
import { useSettings, publicCodeUrl } from "@/lib/settings";

export default function QRModal({ code, onClose }) {
  const wrapRef = useRef(null);
  const { settings } = useSettings();
  const url = useMemo(() => publicCodeUrl(settings, code.code), [settings, code.code]);

  const downloadPNG = () => {
    const canvas = wrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `${(settings.brand_name || "verifine").toLowerCase().replace(/\s+/g, "-")}-${code.code}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)" }}
      data-testid="qr-modal"
      onClick={onClose}
    >
      <div
        className="glass rounded-2xl p-8 max-w-md w-full fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="overline text-[var(--vault-text-muted)]">QR Code</p>
            <h3 className="font-display text-2xl mt-1">{code.label || code.code}</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/5" data-testid="qr-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div ref={wrapRef} className="bg-white p-6 rounded-lg flex justify-center mb-6">
          <QRCodeCanvas
            value={url}
            size={256}
            level="H"
            includeMargin={false}
            fgColor="#0A0A0C"
            bgColor="#FFFFFF"
            data-testid="qr-canvas"
          />
        </div>

        <div className="space-y-3">
          <div
            className="rounded-md p-3 text-center"
            style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }}
          >
            <p className="overline text-[var(--vault-text-muted)] mb-1">Code</p>
            <p className="code-chip inline-block">{code.code}</p>
          </div>
          <div
            className="rounded-md p-3"
            style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }}
          >
            <p className="overline text-[var(--vault-text-muted)] mb-2">Link</p>
            <p className="text-xs font-mono-vault break-all text-[var(--vault-text-muted)]">{url}</p>
          </div>
        </div>

        <div className="flex gap-2 mt-6">
          <button onClick={copyUrl} className="btn-ghost flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm" data-testid="qr-copy-link">
            <Copy className="w-4 h-4" /> Copy link
          </button>
          <button onClick={downloadPNG} className="btn-gold flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm" data-testid="qr-download">
            <Download className="w-4 h-4" /> Download PNG
          </button>
        </div>

        <p className="overline text-[var(--vault-text-faint)] text-center mt-5">
          Use this link to program the NFC tag on the bottle
        </p>
      </div>
    </div>
  );
}
